/* UltraMANTIC product videos v2.1: visible autoplay and two accessible controls. */
(() => {
  'use strict';
  if (window.ultraProductVideoAutoplay) return;
  const main = document.getElementById('MainContent');
  if (!main || main.dataset.template?.split('.')[0] !== 'product') return;
  window.ultraProductVideoAutoplay = true;

  const selector = 'video, iframe[src*="youtube.com/embed/"], iframe[src*="youtube-nocookie.com/embed/"]';
  const states = new Map();
  let youtubeReady;

  const style = document.createElement('style');
  style.textContent = `
    .ultra-video-controls { display:flex; justify-content:flex-end; align-items:center; gap:8px; margin-top:8px; }
    .ultra-video-controls[hidden] { display:none; }
    .ultra-video-controls__button { display:inline-flex; align-items:center; justify-content:center; flex:0 0 40px; width:40px; height:40px; min-width:40px; padding:0; margin:0; border:1px solid rgba(255,255,255,.2); border-radius:50%; background:#1c1c1e; color:#fff; cursor:pointer; line-height:1; box-shadow:none; -webkit-tap-highlight-color:transparent; }
    .ultra-video-controls__button:hover { background:#38383a; }
    .ultra-video-controls__button:focus-visible { outline:2px solid #888; outline-offset:3px; }
    .ultra-video-controls__button:disabled { opacity:.45; cursor:wait; }
    .ultra-video-controls__button svg { display:block; width:20px; height:20px; pointer-events:none; }
    @media (max-width:749px) { .ultra-video-controls__button { width:44px; height:44px; min-width:44px; flex-basis:44px; } }
  `;
  document.head.append(style);
  const icons = {
    play: '<path d="M9 5l11 7-11 7z" fill="currentColor" stroke="none"/>',
    pause: '<path d="M8 5v14M16 5v14" stroke-width="4"/>',
    muted: '<path d="M11 5L6 9H3v6h3l5 4z"/><path d="M16 9l6 6m0-6l-6 6"/>',
    sound: '<path d="M11 5L6 9H3v6h3l5 4z"/><path d="M15 8a6 6 0 010 8m3-11a10 10 0 010 14"/>'
  };

  function isPlaying(state) {
    if (state.native) return !state.element.paused;
    if (!state.ready) return false;
    const current = state.player.getPlayerState();
    return current === 1 || current === 3;
  }

  function renderControls(state) {
    if (!state.buttons) return;
    const playing = isPlaying(state);
    const muted = state.native ? state.element.muted || state.element.volume === 0 : state.muted;
    const renderKey = `${playing}:${muted}:${state.native || state.ready}`;
    if (state.renderKey === renderKey) return;
    state.renderKey = renderKey;
    for (const [button, label, icon] of [
      [state.buttons.play, playing ? 'Pause video' : 'Play video', playing ? 'pause' : 'play'],
      [state.buttons.sound, muted ? 'Unmute video' : 'Mute video', muted ? 'muted' : 'sound']
    ]) {
      button.disabled = !state.native && !state.ready;
      button.setAttribute('aria-label', `${label}: ${state.title}`);
      button.title = label;
      button.innerHTML = `<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.7" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">${icons[icon]}</svg>`;
    }
  }

  function addControls(state) {
    const element = state.element;
    const section = element.closest('.ultra-story-row, .ultra-video-card, .shopify-section');
    state.title = section?.querySelector('h2, h3')?.textContent.trim() || element.title || 'Product video';
    const controls = document.createElement('div');
    controls.className = 'ultra-video-controls';
    controls.setAttribute('role', 'group');
    controls.setAttribute('aria-label', `Video controls: ${state.title}`);
    const play = document.createElement('button');
    const sound = document.createElement('button');
    for (const button of [play, sound]) {
      button.type = 'button';
      button.className = 'ultra-video-controls__button';
      controls.append(button);
    }
    state.controls = controls;
    state.buttons = { play, sound };
    // Place controls outside fixed-aspect-ratio containers and outside the YouTube player.
    const media = element.closest('.ultra-story-row__external-video, .ultra-video-card__media') || element;
    media.after(controls);
    play.addEventListener('click', () => {
      if (isPlaying(state)) {
        state.userPaused = true;
        state.automaticPause = false;
        if (state.native) element.pause();
        else state.player.pauseVideo();
      } else {
        state.userPaused = false;
        state.blocked = false;
        sync(state);
      }
      renderControls(state);
    });
    sound.addEventListener('click', () => {
      if (state.native) {
        element.muted = !(element.muted || element.volume === 0);
        if (!element.muted && element.volume === 0) element.volume = 1;
      } else if (state.ready) {
        state.muted = !state.muted;
        if (state.muted) state.player.mute();
        else {
          state.player.setVolume(100);
          state.player.unMute();
        }
      }
      renderControls(state);
    });
    renderControls(state);
  }

  function restoreYouTubeControls(state) {
    if (state.fallback) return;
    state.fallback = true;
    state.blocked = true;
    state.controls.hidden = true;
    const url = new URL(state.element.src);
    url.searchParams.set('controls', '1');
    state.element.src = url.href;
  }

  function loadYouTube() {
    if (window.YT?.Player) return Promise.resolve(window.YT);
    if (youtubeReady) return youtubeReady;
    youtubeReady = new Promise((resolve, reject) => {
      const previous = window.onYouTubeIframeAPIReady;
      const timeout = window.setTimeout(() => reject(new Error('YouTube unavailable')), 20000);
      window.onYouTubeIframeAPIReady = () => {
        window.clearTimeout(timeout);
        resolve(window.YT);
        if (typeof previous === 'function') previous();
      };
      if (!document.querySelector('script[src="https://www.youtube.com/iframe_api"]')) {
        const script = document.createElement('script');
        script.src = 'https://www.youtube.com/iframe_api';
        script.async = true;
        script.onerror = () => {
          window.clearTimeout(timeout);
          reject(new Error('YouTube unavailable'));
        };
        document.head.append(script);
      }
    });
    return youtubeReady;
  }

  function isActive(state) {
    return state.visible && !document.hidden && state.element.isConnected;
  }

  function pause(state) {
    if (state.native) {
      if (!state.element.paused) {
        state.automaticPause = true;
        state.element.pause();
      }
    } else if (state.ready && (state.player.getPlayerState() === 1 || state.player.getPlayerState() === 3)) {
      state.automaticPause = true;
      state.player.pauseVideo();
    }
  }

  function sync(state) {
    if (!isActive(state)) {
      pause(state);
      return;
    }
    if (state.userPaused || state.blocked) return;
    if (state.native) {
      if (!state.element.paused || state.pending) return;
      state.pending = true;
      // Muting on setup permits autoplay; a visitor's later sound choice is preserved.
      Promise.resolve(state.element.play()).catch(error => {
        if (error.name !== 'AbortError') state.blocked = true;
        renderControls(state);
      }).finally(() => {
        state.pending = false;
        if (!isActive(state) || state.userPaused) pause(state);
      });
    } else if (!state.player && !state.loading) {
      setupYouTube(state);
    } else if (state.ready) {
      const current = state.player.getPlayerState();
      if (current !== 1 && current !== 3) state.player.playVideo();
    }
  }

  function setupNative(state) {
    const video = state.element;
    video.autoplay = false;
    video.removeAttribute('autoplay');
    video.loop = true;
    video.controls = false;
    video.removeAttribute('controls');
    video.playsInline = true;
    video.setAttribute('playsinline', '');
    video.defaultMuted = true;
    video.muted = true;
    video.preload = 'metadata';
    video.addEventListener('pause', () => {
      if (state.automaticPause) {
        state.automaticPause = false;
      } else if (isActive(state) && !video.ended && !video.seeking) {
        state.userPaused = true;
      }
      renderControls(state);
    });
    video.addEventListener('play', () => {
      state.userPaused = false;
      state.blocked = false;
      if (!isActive(state)) pause(state);
      renderControls(state);
    });
    video.addEventListener('volumechange', () => renderControls(state));
    video.addEventListener('ended', () => renderControls(state));
    pause(state);
  }

  async function setupYouTube(state) {
    state.loading = true;
    try {
      const YT = await loadYouTube();
      if (!state.element.isConnected) return;
      state.player = new YT.Player(state.element, {
        events: {
          onReady(event) {
            state.player = event.target;
            state.ready = true;
            state.muted = true;
            state.player.mute();
            state.player.setLoop(true);
            renderControls(state);
            sync(state);
          },
          onStateChange(event) {
            state.playing = event.data === YT.PlayerState.PLAYING || event.data === YT.PlayerState.BUFFERING;
            if (event.data === YT.PlayerState.PLAYING) {
              state.userPaused = false;
              state.blocked = false;
              state.automaticPause = false;
              if (!isActive(state)) pause(state);
            } else if (event.data === YT.PlayerState.PAUSED) {
              if (state.automaticPause) state.automaticPause = false;
              else if (isActive(state)) state.userPaused = true;
            } else if (event.data === YT.PlayerState.ENDED && isActive(state) && !state.userPaused) {
              state.player.seekTo(0, true);
              state.player.playVideo();
            }
            renderControls(state);
          },
          onAutoplayBlocked() {
            state.blocked = true;
            state.playing = false;
            renderControls(state);
          },
          onError() { restoreYouTubeControls(state); }
        }
      });
    } catch (_) {
      // Retain a usable provider player if its API cannot load.
      restoreYouTubeControls(state);
    }
  }

  const observer = 'IntersectionObserver' in window ? new IntersectionObserver(entries => {
    for (const entry of entries) {
      const state = states.get(entry.target);
      if (!state) continue;
      // Half the player must be visible, including within a horizontal carousel.
      state.visible = entry.isIntersecting && entry.intersectionRatio >= 0.5;
      sync(state);
    }
  }, { threshold: [0, 0.5, 1] }) : null;

  function register(element) {
    if (states.has(element)) return;
    const state = { element, native: element.tagName === 'VIDEO', visible: false, userPaused: false, automaticPause: false, blocked: false, muted: true, playing: false };
    states.set(element, state);
    if (state.native) {
      setupNative(state);
    } else {
      const url = new URL(element.src, window.location.href);
      const id = url.pathname.split('/').filter(Boolean).pop();
      for (const [key, value] of Object.entries({ enablejsapi: '1', origin: window.location.origin, autoplay: '0', mute: '1', loop: '1', playlist: id, controls: '0', playsinline: '1' })) {
        url.searchParams.set(key, value);
      }
      if (!element.allow.includes('autoplay')) element.allow += '; autoplay';
      element.src = url.href;
    }
    addControls(state);
    observer?.observe(element);
  }

  function scan() {
    main.querySelectorAll(selector).forEach(register);
    for (const [element, state] of states) {
      if (!element.isConnected) {
        pause(state);
        observer?.unobserve(element);
        state.controls.remove();
        states.delete(element);
      }
    }
  }

  scan();
  new MutationObserver(records => {
    if (records.some(record => [...record.addedNodes, ...record.removedNodes].some(node =>
      node.nodeType === 1 && (node.matches?.(selector) || node.querySelector?.(selector))
    ))) scan();
  }).observe(main, { childList: true, subtree: true });
  document.addEventListener('shopify:section:load', scan);
  document.addEventListener('visibilitychange', () => states.forEach(sync));
  window.addEventListener('pagehide', () => states.forEach(pause));
  window.addEventListener('pageshow', () => states.forEach(sync));
  // Multiple YouTube embeds may deliver state events late. Read their cached API
  // state as well so the buttons always follow playback and in-player shortcuts.
  window.setInterval(() => {
    if (document.hidden) return;
    for (const state of states.values()) {
      if (!state.native && state.ready && !state.fallback) {
        state.muted = state.player.isMuted();
        renderControls(state);
      }
    }
  }, 500);
})();
